import ApplicationComponent from "legacy/ApplicationComponent"
import FormGroup from "legacy/FormGroup"
import NextButton from "legacy/boater-reservation-flow/NextButton"
import {
  buildRoute,
  camelCaseToSnakeCase,
  centsToDollars,
  humanize,
} from "legacy/helpers.es6"
import { validatorFactory } from "legacy/validatorFactory"
import _ from "lodash"
import PropTypes from "prop-types"
import React from "react"

import Modal from "src/components/Modal"

/**
 * The namedDiscountsTable displays a list of currently existing discounts and includes modal for adding new discounts.
 * @param {string[]} discountCategories - List of pos categories which discounts can be applied to.
 * @param {string[]} discountTypes  - List of discount types which discounts can be applied to.
 * @param {Object[]} discounts - List of already existing discounts.
 */

class NamedDiscountsTable extends ApplicationComponent {
  constructor(args) {
    super(args)
    this.state = {
      discounts: _.map(this.props.discounts, (discount) =>
        this.formatDiscount(discount)
      ),
      showCreateDiscountModal: false,
      createProcessing: false,
      processingError: null,
      name: "",
      description: "",
      discountType: "",
      discountAmount: null,
      applyTo: [],
      loadingId: 0,
    }
    this.validator = validatorFactory({ autoForceUpdate: this })
    this.isMobile = this.isMobile.bind(this)
    this.toggleCreateDiscountModal = this.toggleCreateDiscountModal.bind(this)
    this.updateSelectedCategories = this.updateSelectedCategories.bind(this)
    this.createNamedDiscount = this.createNamedDiscount.bind(this)
    this.toggleStatus = this.toggleStatus.bind(this)
  }

  isMobile() {
    return window.innerWidth < 738
  }

  clearFormData() {
    this.setState({
      name: "",
      description: "",
      discountType: "",
      discountAmount: null,
      applyTo: [],
    })
  }

  formatDiscount(discount) {
    return {
      id: discount.encodedId,
      name: humanize(discount.name),
      description: humanize(discount.description),
      discount: this.formatDiscountAmount(discount),
      applyTo: discount.categories
        .map((category) => humanize(category))
        .join(", "),
      enabled: discount.status === "enabled",
    }
  }

  formatDiscountAmount(discount) {
    if (discount.kind === "amount_off_per_unit") {
      return centsToDollars(discount.amountOffPerUnit)
    } else if (discount.kind === "percent_off") {
      return `${discount.percentOff * 100}%`
    } else {
      throw new Error("received discount of unknown kind")
    }
  }

  isDiscountType(type) {
    return this.state.discountType === type
  }

  toggleStatus(params) {
    this.setState({ loadingId: params.id })

    $.ajax({
      url: buildRoute($context.updateUrl, { named_discount_id: params.id }),
      type: "patch",
      data: JSON.stringify(params),
      dataType: "json",
      contentType: "application/json",
      success: (data) => {
        this.setState({
          discounts: this.updateDiscounts(data.id, data.enabled),
          loadingId: 0,
        })
      },
      error: (err) => {
        this.setState({ loadingId: 0 })
        alert(err.responseJSON.error)
      },
    })
  }

  updateDiscounts(updatedDiscountId, isEnabled) {
    return this.state.discounts.map((discount) => {
      if (discount.id === updatedDiscountId) {
        discount.enabled = isEnabled
      }
      return discount
    })
  }

  updateSelectedCategories(selectedCategory) {
    if (_.includes(this.state.applyTo, selectedCategory)) {
      this.setState({
        applyTo: _.reject(
          this.state.applyTo,
          (category) => category === selectedCategory
        ),
      })
    } else {
      this.setState({ applyTo: [...this.state.applyTo, selectedCategory] })
    }
  }

  renderDiscount(discount) {
    return (
      <tr key={discount.id}>
        <td>{discount.name}</td>
        <td>{discount.discount}</td>
        <td>{discount.description}</td>
        <td>{discount.applyTo}</td>
        <td>
          {this.props.userCanManage &&
            this.renderToggleStatusButton(discount.id, discount.enabled)}
        </td>
      </tr>
    )
  }

  toggleCreateDiscountModal(display) {
    this.validator.hideMessages()
    this.setState({
      showCreateDiscountModal: display,
    })
    this.clearFormData()
  }

  discountAmountInputType() {
    if (this.isDiscountType("percentOff")) {
      return "percentage"
    } else if (this.isDiscountType("amountOffPerUnit")) {
      return "currency"
    }
  }

  discountAmountValidation() {
    if (this.isDiscountType("percentOff")) {
      return "|numeric|min:1,num|max:100,num"
    } else if (this.isDiscountType("amountOffPerUnit")) {
      return "|numeric|min:0.01,num|max:10000,num"
    } else {
      return ""
    }
  }

  renderCreateDiscountModal() {
    return (
      <Modal
        isOpen={this.state.showCreateDiscountModal}
        onClose={() => this.toggleCreateDiscountModal(false)}
        maxWidth="max-w-[800px]"
      >
        <Modal.Header title="Add new discount" />
        <Modal.Body>
          <FormGroup
            inputType="input"
            label="Name (Customer facing)"
            name="name"
            onChange={this.handleInputChange}
            value={this.state.name}
            validation={this.validator.message(
              "Name",
              this.state.name,
              "required|max:25"
            )}
          />
          <FormGroup
            inputType="textarea"
            label="Description"
            name="description"
            onChange={this.handleInputChange}
            value={this.state.description}
            validation={this.validator.message(
              "Description",
              this.state.description,
              "required"
            )}
          />
          <hr />
          <FormGroup
            inputType="select"
            label="Discount type"
            name="discountType"
            onChange={this.handleInputChange}
            value={this.state.discountType || "not_selected"}
            validation={this.validator.message(
              "Discount Type",
              this.state.discountType,
              "required"
            )}
          >
            <option value="not_selected" disabled={true}>
              Select Type
            </option>
            {_.map(this.props.discountTypes, (discountType) => (
              <option key={discountType} value={discountType}>
                {humanize(camelCaseToSnakeCase(discountType))}
              </option>
            ))}
          </FormGroup>
          <FormGroup
            inputType={this.discountAmountInputType()}
            label="Discount amount"
            name="discountAmount"
            onChange={this.handleInputChange}
            value={this.state.discountAmount || ""}
            validation={this.validator.message(
              "discount",
              this.state.discountAmount,
              `required${this.discountAmountValidation()}`
            )}
          />
          <label className="mb-0">Apply To</label>
          <fieldset className="flex flex-col items-start">
            <legend className="mb-1 border-none text-sm">
              Select which categories this discount can be applied to
            </legend>
            {_.map(this.props.discountCategories, (category) => (
              <FormGroup
                key={category}
                inputType="checkbox"
                name={category}
                value={_.includes(this.state.applyTo, category)}
                onChange={() => this.updateSelectedCategories(category)}
                label={category}
              />
            ))}
          </fieldset>
          {this.state.processingError && (
            <p className="text-danger mt-3">{this.state.processingError}</p>
          )}
        </Modal.Body>
        <Modal.Footer
          cancelBtnText="Cancel"
          confirmBtnText="Create"
          confirmBtnLoading={this.state.createProcessing}
          onClose={() => this.toggleCreateDiscountModal(false)}
          onSubmit={this.createNamedDiscount}
        />
      </Modal>
    )
  }

  createNamedDiscount() {
    this.validator.showMessages()
    if (!this.validator.allValid()) return
    this.setState({
      createProcessing: true,
      processingError: null,
    })

    const postData = JSON.stringify({
      named_discount: {
        name: this.state.name,
        description: this.state.description,
        kind: camelCaseToSnakeCase(this.state.discountType),
        amount_off_per_unit: this.isDiscountType("amountOffPerUnit")
          ? this.state.discountAmount * 100
          : null,
        percent_off: this.isDiscountType("percentOff")
          ? this.state.discountAmount / 100
          : null,
        categories: this.state.applyTo
          ? this.state.applyTo.map((category) =>
              camelCaseToSnakeCase(category).toLowerCase()
            )
          : [],
      },
    })

    $.ajax({
      url: buildRoute($context.createUrl),
      type: "POST",
      data: postData,
      dataType: "json",
      contentType: "application/json",
      success: (discounts) => {
        Amplitude.trackEvent("web_pos:item_discount_complete")
        this.setState({
          discounts: _.map(discounts, (discount) =>
            this.formatDiscount(discount)
          ),
          createProcessing: false,
          processingError: null,
          showCreateDiscountModal: false,
        })
        this.clearFormData()
      },
      error: (err) => {
        this.setState({
          createProcessing: false,
          processingError: err.responseJSON.error,
        })
      },
    })
  }

  renderToggleStatusButton(id, enabled) {
    return (
      <div className={"flex w-full justify-end"}>
        <NextButton
          processing={this.state.loadingId === id}
          click={() => this.toggleStatus({ id, enabled })}
          btnClass={`w-full md:w-32 text-xs md:text-md md:flex p-1 ${
            enabled ? "btn-danger" : "btn-primary"
          }`}
          text={enabled ? "Disable" : "Enable"}
          processingText={this.isMobile() ? "" : "Updating..."}
        />
      </div>
    )
  }

  renderCreateElements() {
    return (
      <div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            Amplitude.trackEvent("web_pos:item_discount_started")
            this.toggleCreateDiscountModal(true)
          }}
        >
          {" "}
          Add new discount
        </button>
        {this.renderCreateDiscountModal()}
        <hr />
      </div>
    )
  }

  render() {
    return (
      <div>
        {this.props.userCanManage && this.renderCreateElements()}
        <table className="table-default table text-xs md:text-sm">
          <thead>
            <tr>
              <th>Name</th>
              <th>Discount</th>
              <th>Description</th>
              <th>Apply To</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {_.map(this.state.discounts, (discount) =>
              this.renderDiscount(discount)
            )}
          </tbody>
        </table>
      </div>
    )
  }
}

NamedDiscountsTable.propTypes = {
  discounts: PropTypes.array.isRequired,
  discountCategories: PropTypes.array.isRequired,
  discountTypes: PropTypes.array.isRequired,
  userCanManage: PropTypes.bool,
}

export default NamedDiscountsTable
